/**
 * @voltxampere/engine — Junction temperature estimator (Sprint 59)
 *
 * Steady-state thermal model (MVP):
 *
 *   T_junction = T_ambient + P × R_thermal
 *
 * Transient temperature dynamics (RC thermal model with `C_thermal
 * × dT/dt = P − (T − T_ambient) / R_thermal`) live in the metadata
 * table for future use but are NOT integrated this sprint —
 * post-launch Sprint 65+ ships the dynamic model.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { lookupThermal, variantFromParams } from "./metadata.js";
import { computeComponentPower } from "./power.js";
/**
 * Compute per-component junction temperature using the steady-state
 * formula. Components without thermal metadata are omitted (no
 * fabricated values). Caller decides how to render the result.
 */
export function estimateJunctionTemperatures(circuit, dc, opts = {}) {
    const ambient = opts.ambientTempC ?? 25;
    const powerMap = computeComponentPower(circuit, dc);
    const out = new Map();
    for (const c of circuit.components) {
        const power = powerMap.get(c.id);
        if (power === undefined)
            continue;
        const variant = variantFromParams(c.params);
        const thermal = lookupThermal(c.type, variant);
        if (thermal === null)
            continue;
        const tJunction = ambient + power.powerW * thermal.rThermal;
        out.set(c.id, {
            componentId: c.id,
            type: c.type,
            powerW: power.powerW,
            junctionTempC: tJunction,
            rThermal: thermal.rThermal,
            tMaxC: thermal.tMax,
            isOverheated: tJunction > thermal.tMax,
        });
    }
    return out;
}
//# sourceMappingURL=temperature.js.map